<script lang="ts">
export default {
  name: 'Tower',
};
</script>

<script setup lang="ts">
import TowerCanvas from './TowerCanvas.vue';
import UILayer from './UILayer.vue';
import TowerContextMenu from './TowerContextMenu.vue';
import ProgramsLibraryPanel from './ProgramsLibraryPanel.vue';
import PublishPolicyModal from '@/components/PublishPolicyModal.vue';
import {
  EditDisplayOptionsState,
  EditTowerInfoModalState,
  PublishPolicyModalState,
  TowerContextMenuState,
} from '@/types/tower/Tower';
import EditDisplayOptionsModal from '@/views/tower/v2/EditDisplayOptionsModal.vue';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { inject, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { getElementWidth, getElementHeight } from '@/utilities/DOM';
import EditTowerModal from '@/views/tower_dashboard/EditTowerModal.vue';
import { UPDATE_TOWER_VERSIONS } from '@/store/CustomEvents';

const store = inject<Store<AppState>>('store');
if (!store) {
  throw new Error('store is undefined');
}

const resizeObserver = ref({} as ResizeObserver);
const width = ref(0);
const height = ref(0);
const twrap = ref<HTMLElement | null>();

// computed
const contextMenuState = computed<TowerContextMenuState>(() => {
  return store.state.towerCanvasState.contextMenuState;
});

const publishPolicyModalState = computed<PublishPolicyModalState>(() => {
  return store.state.towerCanvasState.publishPolicyModalState;
});

const editDisplayOptionsState = computed<EditDisplayOptionsState>(() => {
  return store.state.towerCanvasState.editDisplayOptionsState;
});

const editTowerInfoModalState = computed<EditTowerInfoModalState>(() => {
  return store.state.towerCanvasState.editTowerInfoModalState;
});

// lifecycles
onMounted(() => {
  resizeObserver.value = new ResizeObserver(() => {
    if (!twrap.value) return;

    const pageHeight = window.innerHeight - (store.state.resizerRegistry['header'].height ?? 0);

    twrap.value.style.height = `${pageHeight}px`;

    width.value = getElementWidth(twrap.value);
    height.value = getElementHeight(twrap.value);
  });

  resizeObserver.value.observe(twrap.value as Element);
});

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect();
});

// ---------
// handlers
// ---------
async function handleTowerInfoUpdate(success: boolean): Promise<void> {
  editTowerInfoModalState.value.show = false;

  if (success) {
    // Update tower versions for current program
    dispatchEvent(new CustomEvent(UPDATE_TOWER_VERSIONS));
  }
}
</script>

<template>
  <div class="tower-wrapper" ref="twrap">
    <UILayer />
    <TowerCanvas :canvasDOMWidth="width" :canvasDOMHeight="height" />
    <TowerContextMenu
      :show="contextMenuState.show"
      :left="contextMenuState.left"
      :top="contextMenuState.top"
      :activeObjectId="contextMenuState.activeObjectId"
      :objectType="contextMenuState.objectType" />
    <PublishPolicyModal
      v-if="publishPolicyModalState.show"
      :policy="publishPolicyModalState.policy"
      :policyType="publishPolicyModalState.policyType" />
    <EditDisplayOptionsModal v-if="editDisplayOptionsState.show" :show="editDisplayOptionsState.show" />
    <EditTowerModal
      v-if="editTowerInfoModalState.show"
      :tower-id="editTowerInfoModalState.towerId"
      @close="editTowerInfoModalState.show = false"
      @update="handleTowerInfoUpdate" />
    <ProgramsLibraryPanel />
  </div>
</template>

<style scoped>
.tower-wrapper {
  position: absolute;
  background-color: var(--tower-v2-bg-color);
  width: 100%;
  height: 100%;
}
</style>

<style>
:root {
  --tower-v2-border-color: #b8987f;
  --tower-v2-primary-color: #758491;
  --tower-v2-bg-color: #ddcec2;
  --tower-v2-alt-bg-color: #d7c5b7;
  --tower-v2-hover-color: #d0bbaa;
  --tower-v2-context-menu-bg-color: #404040;
  --tower-v2-context-menu-bg-color-hover: var(--tower-v2-primary-color);
  --tower-v2-tool-selected-color: var(--tower-v2-primary-color);
  --tower-v2-inspector-header: #000000;
  --sidebar-shadow: 0px 100px 80px rgba(0, 0, 0, 0.07), 0px 41.7776px 33.4221px rgba(0, 0, 0, 0.0503198),
    0px 22.3363px 17.869px rgba(0, 0, 0, 0.0417275), 0px 12.5216px 10.0172px rgba(0, 0, 0, 0.035),
    0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725), 0px 2.76726px 2.21381px rgba(0, 0, 0, 0.0196802);
  --zindex-canvas: 1;
  --zindex-wysiwyg: 2;
  --zindex-layerui: 3;
}
</style>
